import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  createCustomerRequest,
  createCustomerSuccess,
  createCustomerFailure,
  updateCustomerFailure,
  updateCustomerSuccess,
} from "../../actions/customer";
import {
  createCustomer,
  updateCustomerDetails,
  updateCustomerAddresses,
  updateCustomerContacts,
} from "../../api/customer";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Snackbar,
} from "@mui/material";
import api from "../../helpers/util";
import CustomerPricing from "./customerPricing";
import CustomerDesc from "./customerDesc";
import ErrorAlertBox from "../general/ErrorAlertBox";
import CustomerAddress from "./customerAddress";

const emptyContact = {
  firstname: "",
  lastname: "",
  email: "",
  phone: "",
  includeinemail: 1,
};

const CreateCustomerData = ({
  handleClose,
  temperature,
  units,
  packTypes,
  storeTypes,
  locations,
  species,
  companies,
  customerId,
  deliveryMethods,
}) => {
  const dispatch = useDispatch();
  const [customerForm, setCustomerForm] = useState({
    code: "",
    name: "",
    abn: null,
    billingstreetaddress: "",
    billingcity: "",
    billingstateprovince: "",
    billingpostalcode: "",
    billingcountryregion: "",
    includeonmailout: 0,
    notes: "",
    customerof: null,
    delstreetaddress: "",
    delcity: "",
    delstate: "",
    delpostalcode: "",
    paymentterms: null,
    deliverymethod: null,
    recurring: "",
    pricegroup: null,
    runnumber: "",
    truck: "",
  });

  const [contacts, setContacts] = useState([{ ...emptyContact }]);

  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showMessage, setShowMessage] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isAlertVisible, setAlertVisible] = useState(false);
  const [alertMsg, setAlertMsg] = useState("");
  // Index of the contact awaiting delete confirmation (null = no dialog)
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState(null);

  const [validationErrors, setValidationErrors] = useState({
    name: "",
    contacts: [],
  });

  const closeAlert = () => {
    setAlertVisible(false);
  };

  useEffect(() => {
    if (customerId) {
      const fetchCustomer = async () => {
        try {
          const {
            data: { result },
          } = await api.get(`/customers/${customerId}`);
          console.log("update", result);

          // Never let the fetched row's "id" land in editable form state -
          // it's only used via the customerId prop/route param. If this
          // component instance were ever reused for a create flow right
          // after an edit, a stray "id" here would ride along in the
          // create POST body and break the not-null id constraint.
          const { id: _omitId, ...resultWithoutId } = result;
          setCustomerForm((prev) => ({ ...prev, ...resultWithoutId }));

          if (Array.isArray(result.contacts) && result.contacts.length > 0) {
            setContacts(result.contacts);
          } else if (
            result.contactfirstname ||
            result.contactlastname ||
            result.emailaddress ||
            result.mobile
          ) {
            // Fall back to the legacy single-contact fields so existing
            // data isn't lost for customers that haven't been re-saved yet.
            setContacts([
              {
                firstname: result.contactfirstname || "",
                lastname: result.contactlastname || "",
                email: result.emailaddress ? result.emailaddress.trim() : "",
                phone: result.mobile || "",
                includeinemail: 1,
              },
            ]);
          } else {
            setContacts([{ ...emptyContact }]);
          }
        } catch (error) {
          setErrorMessage("Error fetching customer details.");
        }
      };
      fetchCustomer();
    }
  }, [customerId]);

  const handleChange = (event, inputName = "") => {
    console.log(inputName, event);
    const { name, value, type, checked } = event.target;
    const newVal = type === "checkbox" ? (checked ? 1 : 0) : value;
    console.log("value", name, newVal);
    // Do NOT trim here - this runs on every keystroke, and trimming a
    // string that momentarily ends in a space (which is every space you
    // type, until the next character arrives) strips it immediately -
    // that's why "Test Company" was collapsing into "TestCompany" as you
    // typed. Trim once, on submit, instead (see handleCreateCustomer /
    // handleSaveDetails), not on every change event.
    setCustomerForm((prevState) => ({
      ...prevState,
      [name]: newVal,
    }));
  };

  const handleContactChange = (index, field, value) => {
    setContacts((prev) =>
      prev.map((contact, i) =>
        i === index ? { ...contact, [field]: value } : contact
      )
    );
  };

  const addContact = () => {
    setContacts((prev) => [...prev, { ...emptyContact }]);
  };

  // Trash icon click: open confirmation dialog instead of deleting straight away
  const removeContact = (index) => {
    setPendingDeleteIndex(index);
  };

  const cancelRemoveContact = () => setPendingDeleteIndex(null);

  // Confirmed: delete immediately. When editing an existing customer this
  // persists to the DB right away (bulk-replace with the remaining
  // contacts) - no separate Save Details click needed. In create mode
  // there's nothing in the DB yet, so it's local state only.
  // Note: no updateCustomerSuccess dispatch here - the customer reducer
  // replaces the whole customer with the payload, so a partial
  // { id, contacts } payload would wipe the other fields in the store.
  const confirmRemoveContact = () => {
    const index = pendingDeleteIndex;
    setPendingDeleteIndex(null);
    if (index === null) return;

    const remaining = contacts.filter((_, i) => i !== index);

    if (customerId) {
      updateCustomerContacts(customerId, remaining)
        .then((updatedContacts) => {
          setContacts(updatedContacts);
          setIsError(false);
          setSuccessMessage("Contact deleted");
          setShowMessage(true);
        })
        .catch(() => {
          setIsError(true);
          setErrorMessage("Error deleting contact");
          setShowMessage(true);
        });
    } else {
      setContacts(remaining);
    }
  };

  const billingArr = {
    billingstreetaddress: "delstreetaddress",
    billingcity: "delcity",
    billingstateprovince: "delstate",
    billingpostalcode: "delpostalcode",
  };

  const setDeliveryAddress = (set, name = "", value = "") => {
    if (set) {
      if (name && value) {
        setCustomerForm((prevState) => ({
          ...prevState,
          [name]: value,
        }));
      } else {
        Object.keys(billingArr).forEach((billingKey) => {
          const billingValue = billingArr[billingKey];
          setCustomerForm((prevState) => ({
            ...prevState,
            [billingValue]: customerForm[billingKey],
          }));
          console.log(`Key: ${billingKey}, Value: ${billingValue}`);
        });
      }
    }
  };

  const validateCustomerDetails = () => {
    const contactErrors = contacts.map((contact) => ({
      firstname: !contact.firstname ? "Please enter Contact First Name." : "",
      email:
        contact.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)
          ? "Please enter a valid email address"
          : "",
    }));

    return {
      name: !customerForm.name ? "Please enter a valid customer name" : "",
      contacts: contactErrors,
    };
  };

  const validateCustomer = () => validateCustomerDetails();

  // CREATE new customer (all fields at once)
  const handleCreateCustomer = () => {
    console.log("Creating customer", customerForm, contacts);
    const customerCreateErrors = validateCustomer();
    const hasValidationErrors =
      Boolean(customerCreateErrors.name) ||
      customerCreateErrors.contacts.some((c) => c.firstname || c.email);
    setValidationErrors(customerCreateErrors);
    if (hasValidationErrors) {
      return;
    }

    dispatch(createCustomerRequest());
    // Trim leading/trailing whitespace once, here at submit time, instead
    // of on every keystroke (which was the bug - see handleChange).
    createCustomer({
      ...customerForm,
      name: customerForm.name.trim(),
      contacts,
    })
      .then((newCustomer) => {
        console.log("New customer created", newCustomer);
        setCustomerForm({
          code: "",
          name: "",
          abn: null,
          billingstreetaddress: "",
          billingcity: "",
          billingstateprovince: "",
          billingpostalcode: "",
          billingcountryregion: "",
          includeonmailout: 0,
          notes: "",
          customerof: null,
          delstreetaddress: "",
          delcity: "",
          delstate: "",
          delpostalcode: "",
          paymentterms: null,
          deliverymethod: null,
          recurring: "",
          pricegroup: null,
          runnumber: "",
          truck: "",
        });
        setContacts([{ ...emptyContact }]);
        setIsError(false);
        setSuccessMessage("Customer created successfully");
        setShowMessage(true);
        dispatch(createCustomerSuccess(newCustomer));
        setTimeout(() => {
          handleClose();
        }, 1500);
      })
      .catch((error) => {
        console.log("error", error);
        setIsError(true);
        setShowMessage(true);
        dispatch(createCustomerFailure(error));
        const serverMessage =
          error?.response?.data?.message ||
          (Array.isArray(error?.response?.data?.errors)
            ? error.response.data.errors.map((e) => e.message).join(", ")
            : null);
        setErrorMessage(serverMessage || "Error in creating the customer");
      });
  };

  // UPDATE customer details tab (name + delivery method) and contacts
  const handleSaveDetails = () => {
    console.log("Saving customer details");
    const customerDetailsErrors = validateCustomerDetails();
    const hasValidationErrors =
      Boolean(customerDetailsErrors.name) ||
      customerDetailsErrors.contacts.some((c) => c.firstname || c.email);
    setValidationErrors(customerDetailsErrors);
    if (hasValidationErrors) {
      return;
    }

    const detailsData = {
      name: (customerForm.name || "").trim(),
      deliverymethod: customerForm.deliverymethod,
    };

    Promise.all([
      updateCustomerDetails(customerId, detailsData),
      updateCustomerContacts(customerId, contacts),
    ])
      .then(([updatedCustomer, updatedContacts]) => {
        console.log(
          "Customer details updated",
          updatedCustomer,
          updatedContacts
        );
        setIsError(false);
        setSuccessMessage("Customer details updated successfully");
        setShowMessage(true);
        dispatch(
          updateCustomerSuccess({
            ...updatedCustomer,
            contacts: updatedContacts,
          })
        );

        setCustomerForm((prev) => ({
          ...prev,
          ...updatedCustomer,
        }));
        setContacts(updatedContacts);
      })
      .catch((error) => {
        console.log("error", error);
        setIsError(true);
        setShowMessage(true);
        dispatch(updateCustomerFailure(error));
        setErrorMessage("Error updating customer details");
      });
  };

  // UPDATE customer addresses tab only
  const handleSaveAddresses = () => {
    console.log("Saving customer addresses");

    const addressData = {
      billingstreetaddress: customerForm.billingstreetaddress,
      billingcity: customerForm.billingcity,
      billingstateprovince: customerForm.billingstateprovince,
      billingpostalcode: customerForm.billingpostalcode,
      delstreetaddress: customerForm.delstreetaddress,
      delcity: customerForm.delcity,
      delstate: customerForm.delstate,
      delpostalcode: customerForm.delpostalcode,
    };

    updateCustomerAddresses(customerId, addressData)
      .then((updatedCustomer) => {
        console.log("Customer addresses updated", updatedCustomer);
        setIsError(false);
        setSuccessMessage("Customer addresses updated successfully");
        setShowMessage(true);
        dispatch(updateCustomerSuccess(updatedCustomer));

        // Update the form with the response to ensure sync
        setCustomerForm((prev) => ({
          ...prev,
          ...updatedCustomer,
        }));
      })
      .catch((error) => {
        console.log("error", error);
        setIsError(true);
        setShowMessage(true);
        dispatch(updateCustomerFailure(error));
        setErrorMessage("Error updating customer addresses");
      });
  };

  const handleCreateCustomerAlert = (isError) => {
    return (
      <div>
        <Snackbar
          open={showMessage}
          autoHideDuration={2000}
          onClose={() => setShowMessage(false)}
          message="Done"
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          sx={{ "& .MuiSnackbar-root": { bottom: "50px" } }}>
          <Alert severity={isError ? "error" : "success"} variant="filled">
            {isError ? errorMessage : successMessage}
          </Alert>
        </Snackbar>
      </div>
    );
  };

  return (
    <>
      <Dialog
        open={true}
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
        sx={{
          overflow: "auto",
          height: "900px",
          minHeight: "900px",
          maxHeight: "900px",
        }}>
        <DialogTitle>
          <div className="mb-2 ms-3">
            <div style={{ fontSize: "1.25rem", fontWeight: 600 }}>
              {customerId ? "Update Customer" : "Create Customer"}
            </div>

            {customerId && (
              <div style={{ fontSize: "0.85rem", color: "#666", marginTop: 4 }}>
                Name: {customerForm?.name}
              </div>
            )}
          </div>

          <ul className="nav nav-pills" id="pills-tab" role="tablist">
            <li className="nav-item" role="presentation">
              <button
                className="nav-link active fs-6"
                id="pills-desc-tab"
                data-bs-toggle="pill"
                data-bs-target="#pills-desc"
                type="button"
                role="tab"
                aria-controls="pills-desc"
                aria-selected="true">
                Customer Details
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className="nav-link fs-6"
                id="pills-address-tab"
                data-bs-toggle="pill"
                data-bs-target="#pills-address"
                type="button"
                role="tab"
                aria-controls="pills-address"
                aria-selected="false">
                Addresses
              </button>
            </li>
          </ul>
        </DialogTitle>
        <DialogContent>
          <div className="create-customer">
            <div className="tab-content" id="pills-tabContent">
              {/* Customer Details Tab */}
              <div
                className="tab-pane fade show active"
                id="pills-desc"
                role="tabpanel"
                aria-labelledby="pills-desc-tab"
                style={{ height: "520px", overflowY: "auto" }}>
                <CustomerDesc
                  handleChange={handleChange}
                  customerForm={customerForm}
                  validationErrors={validationErrors}
                  deliveryMethods={deliveryMethods}
                  contacts={contacts}
                  onContactChange={handleContactChange}
                  onAddContact={addContact}
                  onRemoveContact={removeContact}
                  customerId={customerId}
                />
                {/*  Save button for Details tab (only shown when editing) */}
                {customerId && (
                  <div style={{ marginTop: "20px", textAlign: "right" }}>
                    <Button
                      onClick={handleSaveDetails}
                      variant="contained"
                      color="primary">
                      Save Details
                    </Button>
                  </div>
                )}
              </div>

              {/* Addresses Tab */}
              <div
                className="tab-pane fade"
                id="pills-address"
                role="tabpanel"
                aria-labelledby="pills-address-tab">
                <div className="" style={{ height: "430px" }}>
                  <CustomerAddress
                    handleChange={handleChange}
                    customerForm={customerForm}
                    validationErrors={validationErrors}
                    deliveryMethods={deliveryMethods}
                    setDeliveryAddress={setDeliveryAddress}
                  />
                  {/* Save button for Addresses tab (only shown when editing) */}
                  {customerId && (
                    <div style={{ marginTop: "20px", textAlign: "right" }}>
                      <Button
                        onClick={handleSaveAddresses}
                        variant="contained"
                        color="primary">
                        Save Addresses
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              <div
                className="tab-pane fade"
                id="pills-price"
                role="tabpanel"
                aria-labelledby="pills-price-tab">
                <div className="" style={{ height: "418px" }}>
                  <CustomerPricing
                    handleChange={handleChange}
                    customerForm={customerForm}
                    validationErrors={validationErrors}
                  />
                </div>
              </div>

              <div
                className="tab-pane fade"
                id="pills-spec"
                role="tabpanel"
                aria-labelledby="pills-spec-tab"
                style={{ height: "418px" }}>
                Format Settings
              </div>
              <div
                className="tab-pane fade"
                id="pills-sale"
                role="tabpanel"
                aria-labelledby="pills-sale-tab">
                <div className="" style={{ height: "418px" }}>
                  Sales History
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          {/* Create button (only shown when creating new customer) */}
          {!customerId && (
            <Button onClick={handleCreateCustomer} color="primary">
              Create
            </Button>
          )}
          <Button onClick={handleClose} color="primary">
            {customerId ? "Close" : "Cancel"}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Delete contact confirmation */}
      <Dialog open={pendingDeleteIndex !== null} onClose={cancelRemoveContact}>
        <DialogTitle>Delete contact?</DialogTitle>
        <DialogContent>
          {pendingDeleteIndex !== null && (
            <span>
              {`Delete ${
                [
                  contacts[pendingDeleteIndex]?.firstname,
                  contacts[pendingDeleteIndex]?.lastname,
                ]
                  .filter(Boolean)
                  .join(" ") || "this contact"
              }${
                contacts[pendingDeleteIndex]?.email
                  ? ` (${contacts[pendingDeleteIndex].email})`
                  : ""
              }?`}
              {customerId ? " This will be deleted immediately." : ""}
            </span>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelRemoveContact} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmRemoveContact} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {showMessage && handleCreateCustomerAlert(isError)}
      {isAlertVisible && (
        <ErrorAlertBox message={alertMsg} onClose={closeAlert} />
      )}
    </>
  );
};

export default CreateCustomerData;
